/**
 * Shared Cloudflare Stream HLS helpers (Slideshow + YouTube Gallery).
 */
(function () {
  if (window.CloudflareHls) return;

  function isCloudflareSrc(src) {
    return /cloudflarestream\.com|videodelivery\.net|stream\.cloudflare\.com/i.test(src || '');
  }

  function getManifestUrl(src) {
    try {
      const url = new URL(src, window.location.href);
      const parts = url.pathname.split('/').filter(Boolean);
      if (!parts.length) return null;

      let manifestUrl;

      if (parts.includes('manifest')) {
        manifestUrl = url;
      } else {
        const uid = parts[0];
        if (!uid) return null;
        manifestUrl = new URL(`${url.origin}/${uid}/manifest/video.m3u8`);
        url.searchParams.forEach((value, key) => {
          manifestUrl.searchParams.set(key, value);
        });
      }

      if (!manifestUrl.searchParams.has('clientBandwidthHint')) {
        manifestUrl.searchParams.set('clientBandwidthHint', '8');
      }

      return manifestUrl.toString();
    } catch (err) {
      return null;
    }
  }

  function loadHls(callback) {
    if (window.Hls) {
      callback(window.Hls);
      return;
    }

    const existing = document.querySelector('script[data-cloudflare-hls-lib]');
    if (existing) {
      existing.addEventListener('load', () => {
        callback(window.Hls || null);
      });
      existing.addEventListener('error', () => {
        callback(null);
      });
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/hls.js@1.5.18/dist/hls.min.js';
    script.async = true;
    script.setAttribute('data-cloudflare-hls-lib', 'true');
    script.addEventListener('load', () => {
      callback(window.Hls || null);
    });
    script.addEventListener('error', () => {
      callback(null);
    });
    document.head.appendChild(script);
  }

  function mountIframeFallback(container, src, title) {
    const iframe = document.createElement('iframe');
    try {
      const url = new URL(src, window.location.href);
      const path = url.pathname || '';
      if (/\/embed\/?$/.test(path)) {
        url.pathname = path.replace(/\/embed\/?$/, '/iframe');
      }
      url.searchParams.set('autoplay', 'true');
      url.searchParams.set('preload', 'auto');
      url.searchParams.set('letterboxColor', 'transparent');
      url.searchParams.delete('width');
      url.searchParams.delete('height');
      iframe.src = url.toString();
    } catch (err) {
      iframe.src = src;
    }

    iframe.title = title || 'Video player';
    const allowAttrs = 'accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen';
    iframe.setAttribute('allow', allowAttrs);
    iframe.allow = allowAttrs;
    iframe.setAttribute('allowfullscreen', '');
    iframe.allowFullscreen = true;
    iframe.setAttribute('frameborder', '0');
    iframe.className = 'block w-full h-full';

    container.appendChild(iframe);
    return iframe;
  }

  /**
   * @param {HTMLElement} container
   * @param {string} src
   * @param {object} options
   * @returns {{ video: HTMLVideoElement|null, hls: object|null, destroy: Function }}
   */
  function mountVideo(container, src, options) {
    const opts = options || {};
    const hlsUrl = getManifestUrl(src);
    let hlsInstance = null;
    let video = null;

    const destroy = () => {
      if (hlsInstance) {
        try {
          hlsInstance.destroy();
        } catch (err) {
          // Ignore teardown errors.
        }
        hlsInstance = null;
      }
    };

    if (!hlsUrl) {
      mountIframeFallback(container, src, opts.title);
      if (typeof opts.onReady === 'function') opts.onReady(null);
      return { video: null, hls: null, destroy };
    }

    video = document.createElement('video');
    video.className = opts.videoClass || 'block w-full h-full';
    video.muted = opts.muted !== false;
    video.loop = opts.loop === true;
    video.autoplay = opts.autoplay === true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.preload = opts.preload || 'auto';
    video.controls = opts.controls === true;
    if (opts.objectFit) video.style.objectFit = opts.objectFit;
    if (opts.title) video.setAttribute('aria-label', opts.title);
    if (opts.controlsList) video.setAttribute('controlslist', opts.controlsList);

    container.appendChild(video);

    const playSafe = () => {
      const result = video.play();
      if (result && typeof result.catch === 'function') {
        result.catch(() => {});
      }
    };

    const markPlaying = () => {
      if (typeof opts.onPlaying === 'function') opts.onPlaying(video);
    };

    const attachNative = () => {
      video.src = hlsUrl;
      video.addEventListener('loadeddata', markPlaying, { once: true });
      video.addEventListener('playing', markPlaying, { once: true });
      if (opts.autoplay) playSafe();
      if (typeof opts.onReady === 'function') opts.onReady(video);
    };

    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      attachNative();
      return { video, hls: null, destroy };
    }

    loadHls((Hls) => {
      if (Hls && Hls.isSupported()) {
        hlsInstance = new Hls({
          enableWorker: true,
          abrEwmaDefaultEstimate: 10000000,
          startLevel: -1
        });
        hlsInstance.loadSource(hlsUrl);
        hlsInstance.attachMedia(video);
        hlsInstance.on(Hls.Events.MANIFEST_PARSED, () => {
          const levels = hlsInstance.levels || [];
          let target = -1;
          for (let i = 0; i < levels.length; i += 1) {
            if ((levels[i].height || 0) >= 1080) target = i;
          }
          if (target < 0 && levels.length) target = levels.length - 1;
          if (target >= 0) {
            hlsInstance.currentLevel = target;
            hlsInstance.loadLevel = target;
          }
          if (opts.autoplay) playSafe();
        });
        hlsInstance.on(Hls.Events.ERROR, (_event, data) => {
          if (data && data.fatal) {
            // eslint-disable-next-line no-console
            console.warn('Cloudflare HLS playback failed', data);
            destroy();
            video.remove();
            mountIframeFallback(container, src, opts.title);
          }
        });
        video.addEventListener('playing', markPlaying, { once: true });
        if (typeof opts.onReady === 'function') opts.onReady(video);
        return;
      }

      attachNative();
    });

    return { video, hls: hlsInstance, destroy };
  }

  window.CloudflareHls = {
    isCloudflareSrc,
    getManifestUrl,
    loadHls,
    mountVideo,
    mountIframeFallback
  };

  document.dispatchEvent(new CustomEvent('cloudflare-hls:ready'));
})();
